/**
 * Manages the local file list state for the NAR letter upload modal —
 * file names, uploaded File objects, and add/delete/replace handlers.
 * @module useFileListState
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useRef, useState } from "react";
import { ToastMessages } from "../../../../components/toasts/toastMessages";

/**
 * Returns true when every uploaded file is the Firebase Storage letter from the dropdown.
 *
 * @param files - Current uploaded file list.
 * @param storageFile - File fetched from storage via dropdown selection, if any.
 * @returns Whether the letter was selected from the dropdown (not manually uploaded).
 */
const computeIsLetterSelectedFromDropdown = (
  files: File[],
  storageFile: File | null,
): boolean => {
  if (!storageFile || files.length === 0) return false;
  return files.every((file) => file === storageFile);
};

/**
 * Encapsulates file list state and manipulation for the upload modal.
 *
 * @returns fileNames, uploadedFiles, fileInputRef, and handlers for
 *   change, delete, and add-from-storage operations.
 */
export const useFileListState = () => {
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null!);
  const storageFileRef = useRef<{ file: File; name: string } | null>(null);

  const isLetterSelectedFromDropdown = computeIsLetterSelectedFromDropdown(
    uploadedFiles,
    storageFileRef.current?.file ?? null,
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    const duplicateFiles = newFiles.filter((newFile) =>
      uploadedFiles.some((existingFile) => existingFile.name === newFile.name),
    );

    if (duplicateFiles.length > 0) {
      toast.error(ToastMessages.DUPLICATE_FILE_UPLOAD);
      return;
    }

    const uniqueFiles = newFiles.filter(
      (newFile) =>
        !uploadedFiles.some(
          (existingFile) => existingFile.name === newFile.name,
        ),
    );

    setFileNames((prev) => [...prev, ...uniqueFiles.map((file) => file.name)]);
    setUploadedFiles((prev) => [...prev, ...uniqueFiles]);
  };

  const handleFileDelete = (index: number) => {
    if (storageFileRef.current?.file === uploadedFiles[index]) {
      storageFileRef.current = null;
    }

    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFileNames((prevNames) => prevNames.filter((_, i) => i !== index));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const addFileFromStorage = useCallback((file: File, displayName?: string) => {
    const prev = storageFileRef.current;
    const name = displayName ?? file.name;
    storageFileRef.current = { file, name };
    console.log("[addFileFromStorage] started", {
      replacingStorageFile: Boolean(prev),
      bytes: file.size,
      hasDisplayName: displayName !== undefined,
      hasName: Boolean(name),
    });

    setUploadedFiles((prevFiles) => {
      const filtered = prev
        ? prevFiles.filter((f) => f !== prev.file)
        : prevFiles;
      console.log("[addFileFromStorage] uploaded files update", {
        beforeCount: prevFiles.length,
        removedCount: prevFiles.length - filtered.length,
        afterCount: filtered.length + 1,
      });
      return [...filtered, file];
    });

    setFileNames((prevNames) => {
      if (prev) {
        const idx = prevNames.indexOf(prev.name);
        console.log("[addFileFromStorage] previous display name lookup", {
          found: idx !== -1,
          index: idx,
          matchingNameCount: prevNames.filter(
            (existing) => existing === prev.name,
          ).length,
        });
        if (idx !== -1) {
          const updated = [...prevNames];
          updated.splice(idx, 1);
          console.log("[addFileFromStorage] display names update", {
            beforeCount: prevNames.length,
            removedCount: 1,
            afterCount: updated.length + 1,
          });
          return [...updated, name];
        }
      }
      console.log("[addFileFromStorage] display names update", {
        beforeCount: prevNames.length,
        removedCount: 0,
        afterCount: prevNames.length + 1,
      });
      return [...prevNames, name];
    });
    console.log("[addFileFromStorage] state updates scheduled");
  }, []);

  return {
    fileNames,
    uploadedFiles,
    isLetterSelectedFromDropdown,
    fileInputRef,
    handleFileChange,
    handleFileDelete,
    addFileFromStorage,
  };
};
